import { useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentRefinements } from 'react-instantsearch';
import { transformFilterCombination } from './getGtmFilterData';
import { useEvents } from './useEvents';
import { useFrame } from '~/shared/utils';

type CurrentRefinements = ReturnType<typeof useCurrentRefinements>['items'];

type PendingFilter = {
    attribute: string;
    value: string;
    isRefinement: boolean;
    refinementsAtTrack: CurrentRefinements;
};

type UseFilterTrackingOptions = {
    enabled?: boolean;
};

type TrackFilterOptions = {
    isRefinement?: boolean;
    replace?: boolean;
    isRefined?: boolean;
};

export const useFilterTracking = ({ enabled = true }: UseFilterTrackingOptions = {}) => {
    const { data: frame } = useFrame();
    const { filterTrackingEvent } = useEvents(frame);
    const { items: currentRefinements } = useCurrentRefinements();
    const pendingFiltersRef = useRef<PendingFilter[]>([]);
    const latestRefinementsRef = useRef(currentRefinements);
    const [trackCount, setTrackCount] = useState(0);

    latestRefinementsRef.current = currentRefinements;

    const trackFilter = useCallback(
        (
            attribute: string,
            value: string,
            { isRefinement = true, replace = false, isRefined = false }: TrackFilterOptions = {},
        ) => {
            const pendingFilters = pendingFiltersRef.current;
            const isUnpick = pendingFilters.some(
                (filter) => filter.attribute === attribute && filter.value === value,
            );

            const remainingFilters = pendingFilters.filter(
                (filter) => filter.attribute !== attribute || (!replace && filter.value !== value),
            );

            pendingFiltersRef.current =
                isRefined || (isUnpick && !replace)
                    ? remainingFilters
                    : [
                          ...remainingFilters,
                          {
                              attribute,
                              value,
                              isRefinement,
                              refinementsAtTrack: latestRefinementsRef.current,
                          },
                      ];

            if (!isRefinement) setTrackCount((count) => count + 1);
        },
        [],
    );

    useEffect(() => {
        if (!pendingFiltersRef.current.length || !enabled) return;

        const isApplied = (filter: PendingFilter) =>
            !filter.isRefinement || currentRefinements !== filter.refinementsAtTrack;

        pendingFiltersRef.current.filter(isApplied).forEach((filter) => {
            const { filterCombination, filterType } = transformFilterCombination(
                filter.attribute,
                currentRefinements,
            );

            filterTrackingEvent({
                filter_type: filterType,
                filter_applied: filter.value,
                filter_combination: filterCombination,
            });
        });

        pendingFiltersRef.current = pendingFiltersRef.current.filter(
            (filter) => !isApplied(filter),
        );
    }, [currentRefinements, filterTrackingEvent, enabled, trackCount]);

    return { trackFilter };
};
